import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { generateSchema, type FieldMapping } from '@/lib/admin/field-utils';

interface SchemaPreviewProps {
  fields: FieldMapping[];
}

export function SchemaPreview({ fields }: SchemaPreviewProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const schema = useMemo(() => {
    if (fields.length === 0) return null;
    return generateSchema(fields);
  }, [fields]);

  const schemaJson = useMemo(() => {
    if (!schema) return '';
    return JSON.stringify(schema, null, 2);
  }, [schema]);

  const handleCopy = async () => {
    if (!schemaJson) return;
    await navigator.clipboard.writeText(schemaJson);
    setCopied(true);
    toast({ title: 'Schema copied to clipboard' });
    setTimeout(() => setCopied(false), 2000);
  };

  if (fields.length === 0) {
    return (
      <div className="border-2 border-dashed rounded-lg p-8 text-center">
        <p className="text-muted-foreground">Add fields to see the generated schema</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div>
          <h4 className="text-sm font-medium">Generated JSON Schema</h4>
          <p className="text-xs text-muted-foreground">
            Auto-generated from {fields.length} field{fields.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleCopy}>
          {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>

      <ScrollArea className="h-[400px] border rounded-lg">
        <pre className="p-4 text-sm font-mono whitespace-pre overflow-x-auto bg-muted/30">
          {schemaJson}
        </pre>
      </ScrollArea>
    </div>
  );
}
